import { useEffect, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { PricingPlans } from "@/components/pricing-plans"
import type { BillingPeriod, PricingPlanId } from "@/components/pricing-plans"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button, buttonVariants } from "@/components/ui/button"
import { videoClient } from "@/lib/video-client"
import { usePlanCheckout } from "@/hooks/use-plan-checkout"
import { Separator } from "@/components/ui/separator"
import { BillingInvoices } from "@/components/billing-invoices"

export const Route = createFileRoute("/app/billing")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    checkout?: "success" | "cancelled"
    session_id?: string
    period?: BillingPeriod
    plan?: PricingPlanId
  } => ({
    plan:
      search.plan === "basic" ||
      search.plan === "pro" ||
      search.plan === "high_volume"
        ? search.plan
        : undefined,
    period:
      search.period === "monthly" || search.period === "yearly"
        ? search.period
        : undefined,
    checkout:
      search.checkout === "success" || search.checkout === "cancelled"
        ? search.checkout
        : undefined,
    session_id:
      typeof search.session_id === "string" &&
      /^cs_(test|live)_[A-Za-z0-9_]+$/.test(search.session_id) &&
      search.session_id.length <= 255
        ? search.session_id
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Billing | intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BillingPage,
})

function BillingPage() {
  const search = Route.useSearch()
  const { pending, error, setError, choose } = usePlanCheckout()
  const [message, setMessage] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    setError(null)
    setMessage(null)
    if (search.checkout === "success") {
      if (!search.session_id)
        setError(
          "The checkout session is missing or invalid. Payment could not be verified."
        )
      else {
        setMessage("Checking your payment with Stripe...")
        void videoClient.billing
          .checkoutStatus({ sessionId: search.session_id })
          .then((session) => {
            if (!active) return
            if (session.activated)
              window.dispatchEvent(new Event("billing-plan-updated"))
            setMessage(
              session.activated
                ? null
                : "Your plan has not been activated from this checkout. Payment may be pending or a newer checkout may already be applied. Try checking again."
            )
          })
          .catch(() => {
            if (active) {
              setMessage(null)
              setError(
                "Could not verify this payment. Check your connection and try again."
              )
            }
          })
      }
    }
    return () => {
      active = false
    }
  }, [search.checkout, search.session_id, retry, setError])

  return (
    <main className="app-main" aria-labelledby="billing-title">
      <div className="pricing-intro">
        <Link
          to="/app"
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "app-video-back self-start",
          })}
        >
          <ArrowLeft data-icon="inline-start" aria-hidden="true" />
          Back to videos
        </Link>
        <h1 id="billing-title">Make room for your next videos.</h1>
        <p>
          Unlock every generated short and keep the same editing tools on every
          paid plan. Choose the video allowance that fits your schedule.
        </p>
      </div>
      {(message || error) && (
        <div className="flex flex-col gap-4">
          {message && (
            <Alert role="status">
              <AlertTitle>Checkout status</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertTitle>Billing needs attention</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {(error || (message && search.checkout === "success")) && (
            <Button
              variant="outline"
              className="self-start"
              disabled={Boolean(pending)}
              onClick={() => setRetry((value) => value + 1)}
            >
              Check again
            </Button>
          )}
        </div>
      )}
      <PricingPlans
        initialPeriod={search.period}
        selectedPlan={search.plan}
        onChoose={(plan, period) => {
          void choose(plan, period)
        }}
        pending={pending}
      />
      <Separator />
      <BillingInvoices />
    </main>
  )
}
